import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Award, Plus, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { resetUploadDocument, uploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { selectDocumentUpload } from "@shared-kernel/application/use-cases/document-upload/selectors/document-upload-selectors";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PDF_FILETYPE, PDF_MAX_FILE_SIZE } from "@shared-kernel/domain/types/file-validation";
import { TeachingMethodologiesField } from "@shared-kernel/primary/shared/teaching-methodologies-field/teaching-methodologies-field";

const UPLOAD_CONTEXT = { asAdmin: false, role: ROLES.TEACHER } as const;

type ResumeField = "teacherResumeUrl" | "artistResumeUrl";

const CertificationsField = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "certifications", keyName: "fieldId" });

  return (
    <FormField
      name="certifications"
      render={() => (
        <FormItem>
          <FormLabel>Certifications et diplômes</FormLabel>
          <FormDescription>
            Toute reconnaissance formelle de votre parcours — académique, professionnelle, institutionnelle, … Elle figure possiblement déjà
            dans votre bio/CV mais elle doit être reprise ici pour permettre aux élèves de filtrer leurs recherches.
          </FormDescription>
          <div className="space-y-3">
            {fields.map((field, rowIndex) => (
              <div
                key={(field as { fieldId: string }).fieldId}
                className="flex items-center gap-3 rounded-md border p-3"
                data-testid={`certification-row-${rowIndex}`}
              >
                <Award className="size-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium">Certification {rowIndex + 1}</div>
                  <FormField
                    name={`certifications.${rowIndex}`}
                    render={({ field: itemField }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...itemField}
                            value={itemField.value ?? ""}
                            placeholder="Intitulé de la certification"
                            data-testid={`certification-input-${rowIndex}`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(rowIndex)}
                  className="size-8"
                  data-testid={`certification-remove-${rowIndex}`}
                >
                  <Trash2 className="size-4" color="red" />
                  <span className="sr-only">Supprimer cette certification</span>
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="w-full" onClick={() => append("")} data-testid="certification-add">
              <Plus className="size-4" />
              Ajouter une certification
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

interface ResumeUploadProps {
  name: ResumeField;
  label: string;
  description: string;
  required: boolean;
  isUploading: boolean;
  onUpload: (field: ResumeField, file: File) => void;
}

const ResumeUploadField = ({ name, label, description, required, isUploading, onUpload }: ResumeUploadProps) => {
  const { setValue, setError, clearErrors } = useFormContext();

  return (
    <FormField
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem>
          <FormLabel>{`${label}${required ? "" : " (optionnel)"}`}</FormLabel>
          <FormDescription>{description}</FormDescription>
          <UploadButton
            processing={isUploading ? "pending" : "idle"}
            buttonText="Télécharger un document (pdf)"
            onFileSelect={file => {
              clearErrors(name);
              onUpload(name, file);
            }}
            onError={() => setError(name, { type: "manual", message: "Veuillez télécharger un fichier PDF de 10 Mo max" })}
            accept={PDF_FILETYPE}
            maxSize={PDF_MAX_FILE_SIZE}
            value={field.value || null}
            onFileDelete={() => {
              clearErrors(name);
              setValue(name, "", { shouldDirty: true });
            }}
            helpText="Télécharger un document PDF de 10 Mo max"
            error={error?.message}
            data-testid={`${name}-upload`}
          />
        </FormItem>
      )}
    />
  );
};

export const TeacherPedagogySubform = () => {
  const { setValue, setError } = useFormContext();
  const dispatch = useAppDispatch();
  const { url, processing } = useAppSelector(selectDocumentUpload);
  const [activeResumeField, setActiveResumeField] = useState<ResumeField | null>(null);

  const handleResumeUpload = (field: ResumeField, file: File) => {
    setActiveResumeField(field);
    dispatch(uploadDocument({ document: file, ...UPLOAD_CONTEXT }));
  };

  useEffect(() => {
    if (activeResumeField === null) return;
    if (processing === "success" && url) {
      setValue(activeResumeField, url, { shouldDirty: true, shouldValidate: true });
      setActiveResumeField(null);
      dispatch(resetUploadDocument());
    } else if (processing === "failed") {
      setError(activeResumeField, { type: "manual", message: "Échec du téléchargement. Le PDF est invalide ou trop volumineux." });
      setActiveResumeField(null);
      dispatch(resetUploadDocument());
    }
  }, [processing, url, activeResumeField, setValue, setError, dispatch]);

  return (
    <div className="space-y-6">
      <ResumeUploadField
        name="teacherResumeUrl"
        label="CV pédagogique"
        description="Votre document de référence en tant qu'enseignant. PDF, 10 Mo max."
        required
        isUploading={activeResumeField === "teacherResumeUrl" && processing === "pending"}
        onUpload={handleResumeUpload}
      />
      <ResumeUploadField
        name="artistResumeUrl"
        label="CV artistique"
        description="Votre biographie ou CV artistique, en complément (optionnel). Il permet de valoriser votre parcours, mais reste secondaire par rapport au CV pédagogique. PDF, 10 Mo max."
        required={false}
        isUploading={activeResumeField === "artistResumeUrl" && processing === "pending"}
        onUpload={handleResumeUpload}
      />
      <CertificationsField />
      <TeachingMethodologiesField name="teachingMethodologies" uploadContext={UPLOAD_CONTEXT} />
    </div>
  );
};
